import React, { useRef, useState } from 'react';
import Iconify from 'src/components/Iconify';
import v2_actionUploadS3 from 'src/actions/v2/global/actionUploadS3';
import { CANVAS_VARIAVEIS } from './canvasUtils';

const FONT_SIZES = [10, 11, 12, 13, 14, 16, 18, 20, 24, 28, 32, 40, 48];

function ToolButton({ icon, title, active, onClick, disabled }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onMouseDown={e => e.preventDefault()}
      onClick={onClick}
      className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors disabled:opacity-30 ${
        active
          ? 'bg-violet-100 dark:bg-violet-500/20 text-[#7C3AED]'
          : 'text-gray-500 dark:text-[#ABABAD] hover:bg-gray-100 dark:hover:bg-[#383838]'
      }`}
    >
      <Iconify icon={icon} width={15} />
    </button>
  );
}

function Divider() {
  return <div className="w-px h-5 bg-gray-200 dark:bg-[#383838] mx-0.5" />;
}

function ColorInput({ value, onChange, title }) {
  return (
    <label className="w-7 h-7 flex items-center justify-center rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-[#383838] transition-colors" title={title}>
      <span className="w-4 h-4 rounded border border-gray-300 dark:border-[#4a4a4a]" style={{ background: value || '#000000' }} />
      <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)} className="sr-only" />
    </label>
  );
}

// Barra da SELEÇÃO MÚLTIPLA: as ações que fazem sentido em bloco (agrupar,
// desagrupar, ordem na pilha, duplicar, trancar, excluir). Estilo/conteúdo continuam
// sendo editados um elemento por vez.
export function SelecaoToolbar({ quantidade, temGrupo, todosTrancados, onAgrupar, onDesagrupar, onDuplicar, onRemover, onMoverZ, onToggleBloqueio }) {
  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838] rounded-xl shadow-md">
      <span className="text-[11px] font-medium text-gray-500 dark:text-[#ABABAD] px-1.5">
        {quantidade} selecionados
      </span>
      <Divider />
      <button
        type="button"
        onClick={onAgrupar}
        title="Agrupar (Ctrl+G)"
        className="h-7 flex items-center gap-1.5 px-2 text-xs font-medium text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10 rounded-md transition-colors"
      >
        <Iconify icon="mdi:group" width={15} />
        Agrupar
      </button>
      {temGrupo && (
        <button
          type="button"
          onClick={onDesagrupar}
          title="Desagrupar (Ctrl+Shift+G)"
          className="h-7 flex items-center gap-1.5 px-2 text-xs font-medium text-gray-500 dark:text-[#ABABAD] hover:bg-gray-100 dark:hover:bg-[#383838] rounded-md transition-colors"
        >
          <Iconify icon="mdi:ungroup" width={15} />
          Desagrupar
        </button>
      )}
      <Divider />
      <ToolButton icon="mdi:arrange-bring-forward" title="Trazer para frente" onClick={() => onMoverZ(1)} />
      <ToolButton icon="mdi:arrange-send-backward" title="Enviar para trás" onClick={() => onMoverZ(-1)} />
      <ToolButton icon="mdi:content-copy" title="Duplicar (Ctrl+D)" onClick={onDuplicar} />
      <ToolButton
        icon={todosTrancados ? 'mdi:lock' : 'mdi:lock-open-variant-outline'}
        title={todosTrancados ? 'Destrancar camadas' : 'Trancar camadas'}
        active={todosTrancados}
        onClick={onToggleBloqueio}
      />
      <button
        type="button"
        title="Remover (Delete)"
        onClick={onRemover}
        className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
      >
        <Iconify icon="mdi:trash-can-outline" width={15} />
      </button>
    </div>
  );
}

// Barra de ferramentas do elemento selecionado no editor canvas.
// `editing` = texto em edição inline (habilita bold/italic via execCommand).
export default function ElementToolbar({ elemento, editing, onChangeEstilo, onChangeConteudo, onDuplicar, onRemover, onMoverZ, onEditarItens, validadeDias, onChangeValidadeDias, onEditarContato, onToggleBloqueio }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  if (!elemento) return null;

  // Camada trancada (cadeado no painel de camadas): nenhuma edição disponível até
  // destrancar — a barra vira só o aviso + o botão de destravar.
  if (elemento.bloqueado) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838] rounded-xl shadow-md">
        <Iconify icon="mdi:lock-outline" width={15} className="text-gray-400" />
        <span className="text-xs text-gray-500 dark:text-[#ABABAD]">Camada trancada</span>
        <button
          type="button"
          onClick={onToggleBloqueio}
          className="h-7 flex items-center gap-1.5 px-2 text-xs font-medium text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10 rounded-md transition-colors"
        >
          <Iconify icon="mdi:lock-open-variant-outline" width={14} />
          Destrancar
        </button>
      </div>
    );
  }

  const { tipo, estilo = {}, conteudo = {} } = elemento;
  const isTexto = tipo === 'texto';
  const isVariavel = tipo === 'variavel';
  const isForma = tipo === 'retangulo' || tipo === 'linha';
  const isImagem = tipo === 'imagem';
  const isData = tipo === 'data';
  const isValidade = tipo === 'validade';
  const isDadosCliente = tipo === 'dados_cliente';
  const temTexto = isTexto || isVariavel || isData || isValidade || isDadosCliente;

  const handleImagemSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await v2_actionUploadS3(formData, '?path=plataforma/propostas');
      const url = res?.data?.data?.location;
      if (url) onChangeConteudo({ url });
    } catch (err) {
      console.error('Erro ao enviar imagem:', err);
    } finally {
      setUploading(false);
    }
  };

  const exec = (cmd) => {
    // Formatação do trecho selecionado durante a edição inline do texto
    document.execCommand(cmd, false, null);
  };

  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838] rounded-xl shadow-md flex-wrap">
      {temTexto && (
        <>
          <select
            value={estilo.fontSize || 14}
            onChange={e => onChangeEstilo({ fontSize: Number(e.target.value) })}
            className="h-7 text-xs rounded-md border border-gray-200 dark:border-[#383838] bg-white dark:bg-[#1A1D21] text-gray-700 dark:text-[#D1D2D3] px-1 focus:outline-none focus:border-[#7C3AED]"
            title="Tamanho da fonte"
          >
            {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          <ToolButton
            icon="mdi:format-bold"
            title="Negrito"
            active={estilo.fontWeight === 'bold'}
            onClick={() => editing && isTexto ? exec('bold') : onChangeEstilo({ fontWeight: estilo.fontWeight === 'bold' ? 'normal' : 'bold' })}
          />
          {isTexto && (
            <ToolButton
              icon="mdi:format-italic"
              title="Itálico"
              active={estilo.fontStyle === 'italic'}
              onClick={() => editing ? exec('italic') : onChangeEstilo({ fontStyle: estilo.fontStyle === 'italic' ? 'normal' : 'italic' })}
            />
          )}
          {editing && isTexto && (
            <ToolButton icon="mdi:format-underline" title="Sublinhado" onClick={() => exec('underline')} />
          )}
          <ColorInput value={estilo.color} onChange={color => onChangeEstilo({ color })} title="Cor do texto" />

          <Divider />

          <ToolButton icon="mdi:format-align-left" title="Alinhar à esquerda" active={(estilo.textAlign || 'left') === 'left'} onClick={() => onChangeEstilo({ textAlign: 'left' })} />
          <ToolButton icon="mdi:format-align-center" title="Centralizar" active={estilo.textAlign === 'center'} onClick={() => onChangeEstilo({ textAlign: 'center' })} />
          <ToolButton icon="mdi:format-align-right" title="Alinhar à direita" active={estilo.textAlign === 'right'} onClick={() => onChangeEstilo({ textAlign: 'right' })} />

          <Divider />
        </>
      )}

      {isVariavel && (
        <>
          <select
            value={conteudo.chave || ''}
            onChange={e => onChangeConteudo({ chave: e.target.value })}
            className="h-7 text-xs rounded-md border border-gray-200 dark:border-[#383838] bg-white dark:bg-[#1A1D21] text-gray-700 dark:text-[#D1D2D3] px-1 focus:outline-none focus:border-[#7C3AED] max-w-[180px]"
            title="Variável"
          >
            {CANVAS_VARIAVEIS.map(v => <option key={v.chave} value={v.chave}>{v.label}</option>)}
          </select>
          <Divider />
        </>
      )}

      {isData && (
        <>
          <input
            type="text"
            placeholder="Cidade (opcional)"
            value={conteudo.cidade || ''}
            onChange={e => onChangeConteudo({ cidade: e.target.value })}
            className="h-7 w-32 text-xs rounded-md border border-gray-200 dark:border-[#383838] bg-white dark:bg-[#1A1D21] text-gray-700 dark:text-[#D1D2D3] px-2 focus:outline-none focus:border-[#7C3AED]"
          />
          <Divider />
        </>
      )}

      {isValidade && onChangeValidadeDias && (
        <>
          <div className="flex items-center gap-1 px-1 text-[11px] text-gray-500 dark:text-[#ABABAD]">
            <span>Válida por</span>
            <input
              type="number"
              min={1}
              value={validadeDias || 15}
              onChange={e => onChangeValidadeDias(Number(e.target.value) || 15)}
              className="h-7 w-14 text-xs text-center rounded-md border border-gray-200 dark:border-[#383838] bg-white dark:bg-[#1A1D21] text-gray-700 dark:text-[#D1D2D3] focus:outline-none focus:border-[#7C3AED]"
            />
            <span>dias</span>
          </div>
          <Divider />
        </>
      )}

      {isDadosCliente && onEditarContato && (
        <>
          <button
            type="button"
            onClick={onEditarContato}
            className="h-7 flex items-center gap-1.5 px-2 text-xs font-medium text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10 rounded-md transition-colors"
          >
            <Iconify icon="mdi:pencil-outline" width={14} />
            Editar contato
          </button>
          <Divider />
        </>
      )}

      {isForma && (
        <>
          <ColorInput value={estilo.background} onChange={background => onChangeEstilo({ background })} title="Cor de preenchimento" />
          {tipo === 'retangulo' && (
            <>
              <ColorInput value={estilo.borderColor} onChange={borderColor => onChangeEstilo({ borderColor })} title="Cor da borda" />
              <select
                value={estilo.borderWidth || 0}
                onChange={e => onChangeEstilo({ borderWidth: Number(e.target.value) })}
                className="h-7 text-xs rounded-md border border-gray-200 dark:border-[#383838] bg-white dark:bg-[#1A1D21] text-gray-700 dark:text-[#D1D2D3] px-1 focus:outline-none"
                title="Espessura da borda"
              >
                {[0, 1, 2, 3, 4].map(w => <option key={w} value={w}>{w === 0 ? 'Sem borda' : `${w}px`}</option>)}
              </select>
              <select
                value={estilo.borderRadius || 0}
                onChange={e => onChangeEstilo({ borderRadius: Number(e.target.value) })}
                className="h-7 text-xs rounded-md border border-gray-200 dark:border-[#383838] bg-white dark:bg-[#1A1D21] text-gray-700 dark:text-[#D1D2D3] px-1 focus:outline-none"
                title="Arredondamento"
              >
                {[0, 4, 8, 12, 16, 24, 999].map(r => <option key={r} value={r}>{r === 999 ? 'Pílula' : `${r}px`}</option>)}
              </select>
            </>
          )}
          <Divider />
        </>
      )}

      {isImagem && (
        <>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="h-7 flex items-center gap-1.5 px-2 text-xs font-medium text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10 rounded-md transition-colors disabled:opacity-50"
          >
            <Iconify icon={uploading ? 'mdi:loading' : 'mdi:upload-outline'} width={14} className={uploading ? 'animate-spin' : ''} />
            {conteudo.url ? 'Trocar imagem' : 'Enviar imagem'}
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImagemSelect} />
          <select
            value={estilo.objectFit || 'contain'}
            onChange={e => onChangeEstilo({ objectFit: e.target.value })}
            className="h-7 text-xs rounded-md border border-gray-200 dark:border-[#383838] bg-white dark:bg-[#1A1D21] text-gray-700 dark:text-[#D1D2D3] px-1 focus:outline-none"
            title="Ajuste da imagem"
          >
            <option value="contain">Conter</option>
            <option value="cover">Preencher</option>
            <option value="fill">Esticar</option>
          </select>
          <select
            value={estilo.borderRadius || 0}
            onChange={e => onChangeEstilo({ borderRadius: Number(e.target.value) })}
            className="h-7 text-xs rounded-md border border-gray-200 dark:border-[#383838] bg-white dark:bg-[#1A1D21] text-gray-700 dark:text-[#D1D2D3] px-1 focus:outline-none"
            title="Arredondamento"
          >
            {[0, 4, 8, 12, 16, 24].map(r => <option key={r} value={r}>{r}px</option>)}
          </select>
          <Divider />
        </>
      )}

      {tipo === 'itens' && onEditarItens && (
        <>
          <button
            type="button"
            onClick={onEditarItens}
            className="h-7 flex items-center gap-1.5 px-2 text-xs font-medium text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10 rounded-md transition-colors"
          >
            <Iconify icon="mdi:pencil-outline" width={14} />
            Editar itens
          </button>
          <Divider />
        </>
      )}

      {/* Opacidade */}
      <div className="flex items-center gap-1.5 px-1" title="Opacidade">
        <Iconify icon="mdi:opacity" width={14} className="text-gray-400" />
        <input
          type="range"
          min={10}
          max={100}
          value={Math.round((estilo.opacity ?? 1) * 100)}
          onChange={e => onChangeEstilo({ opacity: Number(e.target.value) / 100 })}
          className="w-16 accent-[#7C3AED]"
        />
      </div>

      <Divider />

      <ToolButton icon="mdi:arrange-bring-forward" title="Trazer para frente" onClick={() => onMoverZ(1)} />
      <ToolButton icon="mdi:arrange-send-backward" title="Enviar para trás" onClick={() => onMoverZ(-1)} />
      <ToolButton icon="mdi:content-copy" title="Duplicar (Ctrl+D)" onClick={onDuplicar} />
      <ToolButton icon="mdi:lock-open-variant-outline" title="Trancar camada" onClick={onToggleBloqueio} />
      <button
        type="button"
        title="Remover (Delete)"
        onClick={onRemover}
        className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
      >
        <Iconify icon="mdi:trash-can-outline" width={15} />
      </button>
    </div>
  );
}
